import type {
  Friend,
  FriendMatchSummary,
  ChallengePrediction,
  MatchApiResponse,
  BetSelection,
} from '../types/friends.types';
import type { ChallengeWithStats, ChallengePick } from '../../../types/challenges';
import { MATCH_WINNER_MARKET_ID } from '../../challenges/constants/challenges.constants';
import { getEntityName } from './friendsMatchTime.helper';

/** Map 1X2 pick value to outcome. Only marketId 1 (Match Winner) is used for friend match display. */
function pickValueToOutcome(value: string): 'home' | 'draw' | 'away' | null {
  const v = (value ?? '').toLowerCase();
  if (v === 'home' || v === 'draw' || v === 'away') return v;
  return null;
}

/** Build predictionsByUser from challenge.picks (1X2 only). Replaces legacy challenge.predictions. */
function buildPredictionsByUserFromPicks(picks: ChallengePick[]): Record<string, Record<string, ChallengePrediction>> {
  const byUser: Record<string, Record<string, ChallengePrediction>> = {};
  for (const p of picks) {
    if (p.marketId !== MATCH_WINNER_MARKET_ID || !p.userId || !p.matchId) continue;
    const outcome = pickValueToOutcome(p.value);
    if (!byUser[p.userId]) byUser[p.userId] = {};
    byUser[p.userId][p.matchId] = { matchId: p.matchId, userId: p.userId, outcome };
  }
  return byUser;
}

/**
 * Builds friend match data from challenges, matches, and friends list
 * @param friendsList - List of friends
 * @param challengesList - List of challenges with stats
 * @param matchesList - List of matches
 * @param currentUserId - Current user's ID
 * @returns Object containing friendMatches and selections
 */
export const buildFriendMatchData = (
  friendsList: Friend[],
  challengesList: ChallengeWithStats[],
  matchesList: MatchApiResponse[],
  currentUserId: string
): {
  friendMatches: Record<string, FriendMatchSummary[]>;
  selections: Record<string, BetSelection>;
} => {
  const matchLookup = matchesList.reduce<Record<string, MatchApiResponse>>((acc, match) => {
    acc[match.id] = match;
    return acc;
  }, {});

  const friendMatches: Record<string, FriendMatchSummary[]> = {};
  const selections: Record<string, BetSelection> = {};

  friendsList.forEach((friend) => {
    friendMatches[friend.id] = [];
  });

  challengesList.forEach((challenge) => {
    if (!challenge.matchIds?.length) {
      return;
    }

    // Only process challenges where the current user is a participant OR creator
    let participantIds = challenge.participantIds || [];
    const currentUserIsParticipant = participantIds.includes(currentUserId) || challenge.creatorId === currentUserId;
    if (!currentUserIsParticipant) {
      return;
    }

    // Fallback: If challenge name looks like an email and no emails in participantIds,
    // add the challenge name as a potential email match
    // This handles cases where challenges were created with email as the name
    // but the participant record doesn't have the email field set
    if (challenge.name && challenge.name.includes('@')) {
      const challengeNameEmail = challenge.name.toLowerCase().trim();
      if (!participantIds.includes(challenge.name) && !participantIds.includes(challengeNameEmail)) {
        participantIds = [...participantIds, challenge.name, challengeNameEmail];
      }
    }

    // Find friends who are also participants in this challenge
    // For pending friends, check inviteeId/inviterId and email

    const participatingFriends = friendsList.filter((friend) => {
      // First, check if friend.id matches (works for accepted friends)
      if (participantIds.includes(friend.id)) {
        return true;
      }

      // For pending friends, we need to check their actual user IDs and emails
      if (friend.friendshipStatus === 'pending') {
        // Get the actual user ID for this friend (handles pending invitations)
        let friendUserId: string | undefined;

        if (friend.invitationType === 'sent' && friend.inviteeId) {
          // Pending sent invitation - use inviteeId (the person we invited)
          friendUserId = friend.inviteeId;
        } else if (friend.invitationType === 'received' && friend.inviterId) {
          // Pending received invitation - use inviterId (the person who invited us)
          friendUserId = friend.inviterId;
        }

        // Check if the friend's actual user ID is in participantIds
        if (friendUserId && participantIds.includes(friendUserId)) {
          return true;
        }

        // Check if email matches (normalize for comparison)
        // This handles cases where challenge was created with email before user signed up
        if (friend.email) {
          const normalizedEmail = friend.email.toLowerCase().trim();
          const emailPrefix = normalizedEmail.split('@')[0]; // Get part before @
          const normalizedChallengeName = challenge.name?.toLowerCase().trim();

          // Check multiple matching strategies:
          // 1. Direct email match in participantIds
          // 2. Email prefix match in participantIds
          // 3. Challenge name matches email prefix (fallback for old challenges created with email as name)
          // 4. Challenge name matches full email (in case challenge name is the full email)
          const emailMatches =
            participantIds.includes(friend.email) ||
            participantIds.includes(normalizedEmail) ||
            participantIds.some((id) => id.toLowerCase().trim() === normalizedEmail) ||
            participantIds.some((id) => {
              const normalizedId = id.toLowerCase().trim();
              return normalizedId === emailPrefix;
            }) ||
            // Direct challenge name match (handles cases where challenge name is the email prefix or full email)
            (normalizedChallengeName && (
              normalizedChallengeName === emailPrefix ||
              normalizedChallengeName === normalizedEmail ||
              normalizedChallengeName.includes('@') && normalizedChallengeName === normalizedEmail
            ));

          if (emailMatches) {
            return true;
          }
        }

        // For sent invitations, also check inviterEmail if available
        // (in case the challenge was created with the inviter's email)
        if (friend.invitationType === 'sent' && friend.inviterEmail) {
          const normalizedInviterEmail = friend.inviterEmail.toLowerCase().trim();
          const inviterEmailMatches =
            participantIds.includes(friend.inviterEmail) ||
            participantIds.includes(normalizedInviterEmail) ||
            participantIds.some((id) => id.toLowerCase().trim() === normalizedInviterEmail);

          if (inviterEmailMatches) {
            return true;
          }
        }
      } else {
        // Accepted friend - check their ID
        if (participantIds.includes(friend.id)) {
          return true;
        }

        // Also check email for accepted friends (in case challenge was created with email)
        if (friend.email) {
          const normalizedEmail = friend.email.toLowerCase().trim();
          const emailMatches =
            participantIds.includes(friend.email) ||
            participantIds.includes(normalizedEmail) ||
            participantIds.some((id) => id.toLowerCase().trim() === normalizedEmail);

          if (emailMatches) {
            return true;
          }
        }
      }

      return false;
    });

    if (!participatingFriends.length) {
      return;
    }

    const predictionsByUser = buildPredictionsByUserFromPicks(challenge.picks ?? []);

    challenge.matchIds.forEach((matchId: string) => {
      const matchInfo = matchLookup[matchId];
      if (!matchInfo) {
        return;
      }

      const matchDate = matchInfo.date ? new Date(matchInfo.date).toISOString() : new Date().toISOString();
      const myPrediction = predictionsByUser[currentUserId]?.[matchId] || null;
      if (selections[matchId] === undefined) {
        selections[matchId] = myPrediction?.outcome ?? null;
      }

      const odds = matchInfo.odds || {};

      const baseSummary = {
        challengeId: challenge.id,
        challengeName: challenge.name,
        matchId,
        matchDate,
        matchStatus: matchInfo.status,
        league: getEntityName(matchInfo.league, 'League'),
        homeTeam: getEntityName(matchInfo.homeTeam, 'Home'),
        awayTeam: getEntityName(matchInfo.awayTeam, 'Away'),
        odds: {
          home: odds.home ?? odds.homeWin ?? matchInfo.homeOdds,
          draw: odds.draw ?? odds.drawOdds ?? matchInfo.drawOdds,
          away: odds.away ?? odds.awayWin ?? matchInfo.awayOdds,
        },
      };

      participatingFriends.forEach((friend) => {
        // For pending friends, use inviteeId/inviterId to find their predictions
        // Also need to check if predictions are stored under email instead of user ID
        let friendUserId: string | undefined;
        let friendEmail: string | undefined;

        if (friend.friendshipStatus === 'pending') {
          if (friend.invitationType === 'sent' && friend.inviteeId) {
            friendUserId = friend.inviteeId;
          } else if (friend.invitationType === 'received' && friend.inviterId) {
            friendUserId = friend.inviterId;
          }
          friendEmail = friend.email;
        } else {
          friendUserId = friend.id;
          friendEmail = friend.email;
        }

        // Try to find prediction by user ID first
        let friendPrediction = friendUserId ? predictionsByUser[friendUserId]?.[matchId] || null : null;

        // If not found and we have an email, try to find prediction by email
        // (in case predictions are stored with email as userId for pending users)
        if (!friendPrediction && friendEmail) {
          const normalizedEmail = friendEmail.toLowerCase().trim();
          // Check all prediction user IDs to see if any match the email
          Object.keys(predictionsByUser).forEach((predUserId) => {
            if (
              predUserId === friendEmail ||
              predUserId === normalizedEmail ||
              predUserId.toLowerCase().trim() === normalizedEmail
            ) {
              friendPrediction = predictionsByUser[predUserId]?.[matchId] || null;
            }
          });
        }

        friendMatches[friend.id].push({
          ...baseSummary,
          friendBet: friendPrediction?.outcome ?? null,
        });
      });
    });
  });

  Object.keys(friendMatches).forEach((friendId) => {
    friendMatches[friendId].sort((a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime());
  });

  return { friendMatches, selections };
};
